/**
 * DOM - Singleton for DOM manipulation and event management with automatic cleanup
 *
 * SINGLETON: Central DOM utility providing scoped queries, class management, event
 * delegation, and most importantly: automatic event listener cleanup.
 *
 * PUBLIC API NOTE: All public methods auto-inject caller context as first argument.
 * JSDoc @param lists show only user-provided parameters (what you actually pass).
 *
 * MEMORY MANAGEMENT (Critical Pattern):
 * DOM singleton tracks all event listeners attached by Controller instances. When an
 * instance is destroyed, DOM automatically removes all associated event listeners,
 * preventing memory leaks. This is the singleton responsibility pattern in action.
 *
 * KEY FEATURES:
 * - Scoped DOM queries (default scope: this.container)
 * - Class manipulation (addClass, removeClass, hasClass)
 * - Event delegation with automatic cleanup
 * - Viewport and scroll utilities
 * - Transition and animation helpers
 * - Stylesheet loading and management
 * - Element show/hide with transitions
 *
 * CLEANUP PATTERN:
 * ```javascript
 * // Instance attaches listeners via DOM
 * DOM.on({ event: 'click', filter: '.button', handler: this.handleClick });
 *
 * // When instance destroyed, DOM automatically removes all its listeners
 * // No manual cleanup needed!
 * ```
 *
 * @class DOM
 * @extends Class
 * @singleton
 *
 * @example
 * // Access via dependency injection
 * requires: ['DOM']
 *
 * @example
 * // Query scoped to this.container
 * const button = this.DOM.get('.button');  // Queries within this.container
 *
 * @example
 * // Event handling with automatic cleanup
 * this.DOM.on({
 *     event: 'click',
 *     filter: '.button',
 *     handler: this.handleClick,
 *     owner: this  // DOM tracks this for cleanup
 * });
 * // Listener auto-removed when instance destroyed
 */
define("DOM", [`Class`, `module`, `jquery`, `verge`, `modernizr`, `Hammer`], function (Super, Self, jQuery, verge, Modernizr) {
	var UNLOAD_GRACE_PERIOD = 5000, // grace period (ms) before removing an unused <link> tag after last instance dies
		DEFAULT_CUSHION = 20,
		QUICK_EVENTS = [`scroll`, `resize`, `mousemove`, `mousewheel`];

	var registeredPaths = [], // use to keep track of styles that were embedded via pagespeed, so must assume they're present
		instanceBucket = [], // save references to each instance that ingests the DOM dependency
		activeContainers = [], // save references to all the containers that can block DOM events
		activeTransitions = [],
		eventBucket = [], // save references to all active listeners
		KEYWORDS = (function () {
			// determine which set of css keywords to use for the browser
			var testElement = document.createElement(`div`),
				style = testElement.style;

			if (style.WebkitTransition !== undefined) {
				return {
					transitionEvent: `webkitTransitionEnd`,
					transition: `WebkitTransition`,
					transform: `WebkitTransform`,
					filter: `WebkitFilter`,
				};
			} else if (style.MozTransition !== undefined && style.transition === undefined) {
				return {
					transitionEvent: `transitionend`,
					transition: `MozTransition`,
					transform: `MozTransform`,
					filter: undefined, // not well supported
				};
			} else {
				return {
					transitionEvent: `transitionend`,
					transition: `transition`,
					transform: `transform`,
					filter: `filter`,
				};
			}
		})(),
		CSS_KEYS = [`display`, `height`, `height`, `width`, `overflow`, `opacity`], // the list of properties to iterate over during during show/hide
		TRANSITION_KEYS = [
			// the list of properties to clear after the show/hide transition is complete
			KEYWORDS.transform,
			KEYWORDS.transform + `Origin`,
			KEYWORDS.transition + `Duration`,
			KEYWORDS.transition + `Property`,
			KEYWORDS.transition + `TimingFunction`,
		],
		defaultScrollElement;

	return Super.inherit({
		namespace: Self.id,
		singleton: true,
		async prepare(setup) {
			const { data } = this;
			const supports3d = this.calculateSupports3d();
			const supportsPassive = this.calculateSupportsPassive();
			const supportsWebp = await this.calculateSupportsWebp();
			const styleCache = {};

			this.assign(data, { styleCache, supports3d, supportsPassive, supportsWebp });
		},
		api: {
			/**
			 * @method DOM.calculateSupports3d
			 */
			calculateSupports3d: {
				value() {
					const testElement = document.createElement(`div`);
					const rule = `translate3d(1px,1px,1px)`;
					const mozillaRule = `-moz-transform:${rule};`;
					const msRule = `-ms-transform:${rule};`;
					const operaRules = `-o-transform:${rule};`;
					const webkitRule = `-webkit-transform:${rule};`;
					const standardRule = `transform:${rule};`;
					const vendorRule = mozillaRule + msRule + operaRules + webkitRule + standardRule;

					testElement.style.cssText = vendorRule;
					return testElement.style.cssText.indexOf(`translate3d`) >= 0;
				},
			},

			/**
			 * @method DOM.calculateSupportsPassive
			 */
			calculateSupportsPassive: {
				value() {
					// NOTE: see https://github.com/WICG/EventListenerOptions

					let result = false;

					// Test via a getter in the options object to see if the passive property is accessed
					try {
						const opts = Object.defineProperty({}, `passive`, {
							get: () => (result = true),
						});

						window.addEventListener(`test`, null, opts);
						window.removeEventListener(`test`, null, opts);
						return result;
					} catch (e) {
						return false;
					}
				},
			},

			/**
			 * @method DOM.calculateSupportsWebp
			 */
			calculateSupportsWebp: {
				async: true,
				value() {
					return new Promise((resolve) => {
						// pass through the Modernizr result as a promise, strictly requiring a true result
						Modernizr.on(`webp`, (result) => resolve(result === true));
					});
				},
			},

			/**
			 * @method DOM.supports3d
			 */
			supports3d: {
				public: true,
				value() {
					return this.data.supports3d;
				},
			},

			/**
			 * @method DOM.supportsWebp
			 */
			supportsWebp: {
				public: true,
				value() {
					return this.data.supportsWebp;
				},
			},

			/**
			 * Register stylesheet href as already loaded (for server-embedded styles).
			 *
			 * @method DOM#registerHref
			 * @param {Object} options - Registration options
			 * @param {string} options.href - Stylesheet path
			 */
			registerHref: {
				public: true,
				value(context, options) {
					var href = options.href;

					if (!(href.indexOf(`http://`) === 0 || href.indexOf(`https://`) === 0 || href.indexOf(`//`) === 0)) {
						href = this.Config.STATIC_CSS_PATH + href;
					}

					registeredPaths.push(href);
				},
			},

			/**
			 * Load a stylesheet dynamically (with automatic cleanup on destroy).
			 *
			 * Returns a promise representing one of three states:
			 *   1. Already loaded (server-embedded, or a prior request finished) -> resolves immediately.
			 *   2. Previously requested but the <link> is still loading -> resolves when that load finishes.
			 *   3. Not present -> inserts the <link> and resolves when it loads.
			 *
			 * Resolution is driven by the link's load/error events, which fire reliably in our target
			 * browsers (modern Chrome, Safari, Edge). The error event is the safety net: a failed
			 * stylesheet resolves rather than rejecting so callers proceed instead of hanging.
			 *
			 * @method DOM#loadStylesheet
			 * @param {Object} [options] - Loading options
			 * @param {string} [options.href] - Stylesheet path (defaults to /ClassName.css)
			 * @returns {Promise|undefined} Resolves when the stylesheet is ready; undefined when already loaded
			 */
			loadStylesheet: {
				async: true,
				public: true,
				value(context, options) {
					const { Config, data } = this;
					const { STATIC_CSS_PATH } = Config;
					const { namespace } = context;
					const defaultHref = `/${namespace}.css`;

					this.defaults(options, { href: defaultHref });

					const { href: optionsHref } = options;
					const { styleCache } = data;
					const isHttp = optionsHref.indexOf(`http://`) === 0;
					const isHttps = optionsHref.indexOf(`https://`) === 0;
					const isSameProtocol = optionsHref.indexOf(`//`) === 0;
					const isAbsolute = isHttp || isHttps || isSameProtocol;
					const href = isAbsolute ? optionsHref : `${STATIC_CSS_PATH}${optionsHref}`;

					let existing;
					let deferred;

					// (1a) preloaded at initial page load — assumed present and ready, never tracked for removal
					if (this.includes(registeredPaths, href)) {
						return;
					}

					// track this instance so the link is cleaned up once no instances reference it
					if (this.isArray(styleCache[href])) {
						// array already defined, add this context to the list of instances using this stylesheet
						styleCache[href].push(context);
					} else {
						// first request for this stylesheet, create array with this context as the first entry
						styleCache[href] = [context];
					}

					existing = this.get(`link[href="${href}"]`, document);

					if (this.isElement(existing)) {
						// (1b) already settled — either flagged by our load/error handlers below, or present and
						// loaded before we attached (e.g. server-embedded link): a populated `sheet` means
						// the load event already fired and would never fire again for a freshly-attached listener.
						// NOTE: `sheet` only signals success; a previously-failed link is caught by `_cssSettled`.
						if (existing._cssSettled || existing.sheet) {
							existing._cssSettled = true;
							return;
						}
						// (2) link exists but is still in-flight — fall through to attach listeners
					} else {
						// (3) not present — insert the link
						existing = document.createElement(`link`);
						existing.type = `text/css`;
						existing.rel = `stylesheet`;
						existing.href = href;
						document.head.appendChild(existing);
					}

					// states 2 and 3: build a promise resolved by the element's load/error events
					deferred = this.Deferred();

					this.on({
						target: existing,
						event: `load`,
						matchContainer: false,
						handler: function () {
							existing._cssSettled = true;
							deferred.resolve();
						},
					});

					this.on({
						target: existing,
						event: `error`,
						matchContainer: false,
						handler: () => {
							existing._cssSettled = true;
							this.log({
								type: `warning`,
								message: `loadStylesheet(): failed to load ` + href,
							});
							deferred.resolve();
						},
					});

					return this.when({
						promise: deferred.promise,
						fin: function () {
							this.off({ target: existing, event: `load` });
							this.off({ target: existing, event: `error` });
						},
					});
				},
			},

			/** Utility functions */

			/**
			 * Query multiple elements, scoped to caller's container by default.
			 * Returns augmented array with chainable methods.
			 *
			 * @method DOM#select
			 * @param {string} selector - CSS selector
			 * @param {Element} [container] - Search scope (defaults to this.container)
			 * @returns {Element[]} Augmented array with helper methods
			 */
			select: {
				public: true,
				value(context, selector, container) {
					// return an array of elements matching the passed selector
					var _this = this,
						collection;

					if (this.isUndefined(container)) {
						container = context.container;
					}

					collection = jQuery(selector, container).toArray();

					Object.defineProperties(collection, {
						addClass: {
							value: function (className) {
								jQuery(collection).addClass(className);
								return collection;
							},
						},
						removeClass: {
							value: function (className) {
								jQuery(collection).removeClass(className);
								return collection;
							},
						},
						setAttribute: {
							value: function (key, value) {
								jQuery(collection).attr(key, value);
								return collection;
							},
						},
						removeAttribute: {
							value: function (key) {
								jQuery(collection).removeAttr(key);
								return collection;
							},
						},
						remove: {
							value: function () {
								jQuery(collection).remove();
								return collection;
							},
						},
						matches: {
							value: function (selector) {
								// filter out elements that don't match the passed selector
								var result = _this.filter(collection, function (element) {
									return _this.matches(selector, element);
								});

								return context.select(result);
							},
						},
						trigger: {
							value: function (options) {
								_this.trigger({
									target: collection[0],
									event: options.event,
								});

								return collection;
							},
						},
						show: {
							value: function (options) {
								options = _this.defaults(options, {
									owner: context, // pass the owner forward, so it's not forgotten
								});

								options.promises = _this.map(collection, function (element) {
									return _this.show({
										target: element,
										transition: options.transition,
										duration: options.duration,
									});
								});

								return _this.when(options);
							},
						},
						hide: {
							value: function (options) {
								options = _this.defaults(options, {
									owner: context, // pass the owner forward, so it's not forgotten
								});

								options.promises = _this.map(collection, function (element) {
									return _this.hide({
										target: element,
										transition: options.transition,
										duration: options.duration,
									});
								});

								return _this.when(options);
							},
						},
						chain: {
							// return a chainable version of the collection
							value: function () {
								var value = collection.value();
								return context.chain(value);
							},
						},
						value: {
							// return a clean version of the internal value
							value: function () {
								return [].concat(collection); // return a standard Array
							},
						},
					});

					return collection;
				},
			},

			/**
			 * Query a single element, scoped to caller's container by default.
			 *
			 * @method DOM#get
			 * @param {string} selector - CSS selector
			 * @param {Element} [container] - Search scope (defaults to this.container)
			 * @returns {Element|null} First matching element, or null
			 */
			get: {
				public: true,
				value(context, selector, container) {
					if (this.isUndefined(container)) {
						container = context.container;
					}

					const matches = jQuery(selector, container);
					return matches.length > 0 ? matches[0] : null;
				},
			},

			/**
			 * Check if element matches selector.
			 *
			 * @method DOM#matches
			 * @param {string} selector - CSS selector to test
			 * @param {Element} [element] - Element to test (defaults to this.container)
			 * @returns {boolean} True if element matches selector
			 */
			matches: {
				public: true,
				value(context, selector, element) {
					if (this.isUndefined(element)) {
						element = context.container;
					}

					// check whether the element matches the passed selector
					return jQuery(element).is(selector);
				},
			},

			/**
			 * Find closest ancestor matching selector.
			 *
			 * @method DOM#closest
			 * @param {Element} element - Starting element
			 * @param {string} selector - CSS selector for ancestor
			 * @param {Element} [container] - Boundary element (defaults to this.container)
			 * @returns {Element|null} Matching ancestor or null
			 */
			closest: {
				public: true,
				value(context, element, selector, container) {
					var match = jQuery(element).closest(selector)[0];

					if (this.isUndefined(container)) {
						container = context.container;
					}

					if (this.isElement(container)) {
						if (this.containsElement(match, container)) {
							return match;
						} else {
							return null;
						}
					} else {
						return match;
					}
				},
			},

			/**
			 * Get sibling elements matching optional selector.
			 *
			 * @method DOM#siblings
			 * @param {Element} element - Reference element
			 * @param {string} [selector] - Optional CSS selector to filter siblings
			 * @returns {Element[]} Array of sibling elements
			 */
			siblings: {
				public: true,
				value(context, element, selector) {
					return jQuery(element).siblings(selector).toArray();
				},
			},

			/**
			 * Check if container contains element.
			 *
			 * @method DOM#containsElement
			 * @param {Element} element - Element to check
			 * @param {Element} [container] - Container to search (defaults to this.container)
			 * @returns {boolean} True if container contains element
			 */
			containsElement: {
				public: true,
				value(context, element, container) {
					// NOTE: awkward name, so it doesn't conflict with this.contains inherited from Class
					if (!this.isElement(container)) {
						container = context.container;
					}

					return container === element || jQuery.contains(container, element);
				},
			},

			/**
			 * Check if element has class.
			 *
			 * @method DOM#hasClass
			 * @param {string} className - Class name to check
			 * @param {Element} [element] - Element to check (defaults to this.container)
			 * @returns {boolean} True if element has class
			 */
			hasClass: {
				public: true,
				value(context, className, element) {
					if (!this.isElement(element)) {
						element = context.container;
					}

					return jQuery(element).hasClass(className);
				},
			},

			/**
			 * Check if element is visible (has dimensions).
			 *
			 * @method DOM#isVisible
			 * @param {Element} [element] - Element to check (defaults to this.container)
			 * @returns {boolean} True if element takes up space in document
			 */
			isVisible: {
				public: true,
				value(context, element) {
					if (!this.isElement(element) && element !== window) {
						element = context.container;
					}

					if (element === window) {
						return true;
					} else if (!this.isElement(element)) {
						throw new Error(`DOM.isVisible(element) wasn't passed an element.`);
					}

					// check if the element takes up space in the document
					return element.clientWidth > 0 || element.clientHeight > 0 || element.getClientRects().length > 0;
				},
			},

			/**
			 * Check if element is in viewport.
			 *
			 * @method DOM#inViewport
			 * @param {Element|Object} [arg] - Element to check, or options object
			 * @param {Element} [arg.target] - Element to check (defaults to this.container)
			 * @param {number} [arg.cushion=20] - Pixel buffer
			 * @param {string} [arg.direction='both'] - 'vertical', 'horizontal', or 'both'
			 * @param {boolean} [arg.fully=false] - Require entire element in viewport
			 * @returns {boolean} True if element is in viewport
			 */
			inViewport: {
				public: true,
				value(context, arg) {
					var options = {},
						result;

					if (this.isElement(arg)) {
						options.target = arg;
					} else if (this.isObject(arg)) {
						this.defaults(options, arg);
					} else if (!this.isUndefined(arg)) {
						throw new Error(`DOM.inViewport(): Unsupported argument, expected Element | Object | undefined`);
					}

					this.defaults(options, {
						target: context.container, // assume requesting instance's container element
						cushion: DEFAULT_CUSHION,
						direction: `both`, // vertical | horizontal | both
						fully: false, // whether the element needs to be ENTIRELY in the viewport
					});

					if (options.direction === `vertical` || options.direction === `both`) {
						if (options.fully) {
							options.cushion = -1 * options.target.clientHeight;
						}

						result = verge.inY(options.target, options.cushion);
					}

					if (options.direction === `horizontal` || options.direction === `both`) {
						if (options.fully) {
							options.cushion = -1 * options.target.clientWidth;
						}

						if (options.direction === `horizontal`) {
							result = true;
						}

						result = result && verge.inX(options.target, options.cushion);
					}

					return result === true;
				},
			},

			/**
			 * Get viewport dimensions.
			 *
			 * @method DOM#getViewportDimensions
			 * @param {Object} [options] - Options (currently unused)
			 * @returns {Object} Object with width and height getters
			 */
			getViewportDimensions: {
				public: true,
				defaults: {},
				value(context, options) {
					// this.defaults(options, {
					//     target: context.container
					// });

					// var pageContainer = document.getElementById('page'),
					//     overlayContainer = document.getElementById('overlay'),
					//     element;

					// if(this.containsElement(options.target, overlayContainer)) {
					//     element = overlayContainer;
					// } else {
					//     element = pageContainer;
					// }

					return Object.defineProperties(
						{},
						{
							width: {
								get: function () {
									return window.innerWidth;
								},
							},
							height: {
								get: function () {
									return window.innerHeight;
								},
							},
						}
					);
				},
			},

			/**
			 * Check if two elements are overlapping.
			 *
			 * @method DOM#isTouching
			 * @param {Element} element - First element
			 * @param {Element} [element2] - Second element (defaults to this.container)
			 * @returns {boolean} True if bounding boxes overlap
			 */
			isTouching: {
				public: true,
				value(context, element, element2) {
					if (!this.isElement(element2)) {
						element2 = context.container;
					}

					var rect1 = element.getBoundingClientRect(),
						rect2 = element2.getBoundingClientRect(),
						overlappingX = !(rect1.right < rect2.left || rect1.left > rect2.right),
						overlappingY = !(rect1.bottom < rect2.top || rect1.top > rect2.bottom);

					return overlappingX && overlappingY;
				},
			},

			/**
			 * Add classes to element(s).
			 *
			 * @method DOM#addClass
			 * @param {string} className - Space-delimited class names
			 * @param {Element|Element[]} [elements] - Target element(s) (defaults to this.container)
			 * @returns {Element[]} Array of affected elements
			 */
			addClass: {
				public: true,
				value(context, className, elements) {
					if (this.isElement(elements)) {
						elements = [elements];
					} else if (!this.isArray(elements)) {
						elements = [context.container];
					}

					jQuery(elements).addClass(className);
					return elements;
				},
			},

			/**
			 * Remove classes from element(s).
			 *
			 * @method DOM#removeClass
			 * @param {string} className - Space-delimited class names
			 * @param {Element|Element[]} [elements] - Target element(s) (defaults to this.container)
			 * @returns {Element[]} Array of affected elements
			 */
			removeClass: {
				public: true,
				value(context, className, elements) {
					if (this.isElement(elements)) {
						elements = [elements];
					} else if (!this.isArray(elements)) {
						elements = [context.container];
					}

					jQuery(elements).removeClass(className);
					return elements;
				},
			},

			/**
			 * Trigger event on element.
			 *
			 * @method DOM#trigger
			 * @param {Object} options - Trigger options
			 * @param {string} options.event - Event name
			 * @param {Element} [options.target] - Target element
			 * @param {string} [options.selector] - CSS selector for target
			 * @param {Element} [options.container] - Search scope (defaults to this.container)
			 * @returns {Element} Target element
			 */
			trigger: {
				public: true,
				value(context, options) {
					this.defaults(options, {
						container: context.container,
					});

					if (this.isUndefined(options.target)) {
						if (this.isString(options.selector, 1)) {
							options.target = this.get(options.selector, options.container);
						} else {
							options.target = options.container;
						}
					}

					jQuery(options.target).trigger(options.event);
					return options.target;
				},
			},

			/**
			 * Get computed style value for element.
			 *
			 * @method DOM#getStyle
			 * @param {string} style - CSS property name
			 * @param {Element} [target] - Target element (defaults to this.container)
			 * @returns {string} Computed style value
			 */
			getStyle: {
				public: true,
				value(context, style, target) {
					if (!this.isElement(target)) {
						target = context.container;
					}

					return window.getComputedStyle(target, null).getPropertyValue(style);
				},
			},

			/**
			 * Get element dimensions and position.
			 *
			 * @method DOM#getDimensions
			 * @param {Element} [element] - Element to measure (defaults to this.container)
			 * @param {Object} [options] - Measurement options
			 * @param {boolean} [options.includeMargins=false] - Include margins in width/height
			 * @returns {Object} Dimensions object with width, height, top, left, etc.
			 */
			getDimensions: {
				public: true,
				value(context, element, options) {
					var _this = this,
						dimensions = {};

					options = this.defaults(options, {
						includeMargins: false,
					});

					if (!this.isElement(element)) {
						if (this.isElement(context.container)) {
							element = context.container;
						} else {
							throw new Error(`DOM.getDimensions(element): No valid or default element found`);
						}
					}

					Object.defineProperties(dimensions, {
						element: {
							get: function () {
								return element;
							},
						},
						width: {
							get: function () {
								return jQuery(element).outerWidth(options.includeMargins); // include padding, not margin
							},
						},
						scrollWidth: {
							get: function () {
								return element.scrollWidth;
							},
						},
						height: {
							get: function () {
								return jQuery(element).outerHeight(options.includeMargins); // include padding, not margin
							},
						},
						scrollHeight: {
							get: function () {
								return element.scrollHeight;
							},
						},
						top: {
							get: () => {
								return jQuery(element).offset().top;
							},
						},
						left: {
							get: function () {
								return jQuery(element).offset().left;
							},
						},
						bottom: {
							get: function () {
								return dimensions.top + dimensions.height;
							},
						},
						right: {
							get: function () {
								return dimensions.left + dimensions.width;
							},
						},
						paddingTop: {
							get: function () {
								var style = _this.getStyle(`padding-top`, element);
								return parseFloat(style.replace(/[a-z]/gi, ``), 10);
							},
						},
						paddingLeft: {
							get: function () {
								var style = _this.getStyle(`padding-left`, element);
								return parseFloat(style.replace(/[a-z]/gi, ``), 10);
							},
						},
						paddingBottom: {
							get: function () {
								var style = _this.getStyle(`padding-bottom`, element);
								return parseFloat(style.replace(/[a-z]/gi, ``), 10);
							},
						},
						paddingRight: {
							get: function () {
								var style = _this.getStyle(`padding-right`, element);
								return parseFloat(style.replace(/[a-z]/gi, ``), 10);
							},
						},
						viewportTop: {
							get: () => {
								const boundingClientRect = element.getBoundingClientRect();
								const { top } = boundingClientRect;
								return top;
							},
						},
						viewportLeft: {
							get: function () {
								const boundingClientRect = element.getBoundingClientRect();
								const { left } = boundingClientRect;
								return left;
							},
						},
						viewportRight: {
							get: function () {
								return verge.viewportW() - (dimensions.viewportLeft + dimensions.width);
							},
						},
						viewportBottom: {
							get: function () {
								return verge.viewportH() - (dimensions.viewportTop + dimensions.height);
							},
						},
						inViewport: {
							get: function () {
								return verge.inViewport(element, DEFAULT_CUSHION);
							},
						},
						isVisible: {
							get: function () {
								return _this.isVisible(element);
							},
						},
						inDOM: {
							get: function () {
								return _this.containsElement(element, document.documentElement);
							},
						},
						opacity: {
							get: function () {
								var opacityValue = _this.getStyle(`opacity`, element);
								return parseFloat(opacityValue);
							},
						},
						display: {
							get: function () {
								return _this.getStyle(`display`, element);
							},
						},
						overflow: {
							get: function () {
								return _this.getStyle(`overflow`, element);
							},
						},
					});

					return dimensions;
				},
			},

			/**
			 * Remove element from DOM.
			 *
			 * @method DOM#remove
			 * @param {Element} [element] - Element to remove (defaults to this.container)
			 * @returns {Element|null} Removed element or null
			 */
			remove: {
				public: true,
				value(context, element) {
					var target, result;

					if (this.isElement(element)) {
						target = element;
					} else if (this.isUndefined(element)) {
						target = context.container;
					} else {
						throw new Error(`DOM.remove(): Invalid element argument`);
					}

					result = jQuery(target).remove()[0];

					return this.isElement(result) ? result : null;
				},
			},

			/**
			 * Attach event listener with automatic cleanup.
			 *
			 * @method DOM#on
			 * @param {Object} options - Listener configuration
			 * @param {string} options.event - Event name ('click', 'scroll', etc.)
			 * @param {Function} options.handler - Event handler function
			 * @param {Element} [options.target] - Event target (defaults to this.container)
			 * @param {string} [options.filter] - Delegation selector
			 * @param {boolean|string} [options.trigger=false] - Auto-trigger: true or 'visible'
			 * @param {boolean} [options.matchContainer=true] - Require event in this container
			 * @param {boolean} [options.passive=false] - Use passive listener
			 * @returns {Object} Listener object with remove() method
			 */
			on: {
				public: true,
				defaults: {
					filter: null,
					trigger: false,
					matchContainer: true,
					passive: false,
				},
				value(context, options) {
					this.defaults(options, {
						target: context.container,
					});

					const { data } = this;
					const { supportsPassive } = data;

					var _this = this, // for use in unbound function calls
						Hammer = this.require(`Hammer`),
						HAMMER_EVENTS = [`tap`, `swipe`],
						container = this.isElement(options.container) ? options.container : context.container,
						listener = {
							event: options.event,
							target: options.target,
							filter: options.filter,
							handler: options.handler,
							owner: context,
						},
						existingListener = this.find(eventBucket, listener),
						quickHandler,
						fullHandler,
						proxyHandler,
						triggerHandler;

					let hammer;

					if (options.matchContainer && !this.isElement(container)) {
						this.log({
							type: `warning`,
							message: `DOM.on(options.matchContainer = true) is invalid without a container`,
						});

						options.matchContainer = false;
					}

					if (existingListener !== undefined) {
						return existingListener;
					}

					quickHandler = function (evt) {
						if (!context.alive) {
							// zombie listener!
							this.log({
								type: `warning`,
								message: `Ignoring DOM event callback, owner.alive is false`,
							});

							listener.remove();
							return;
						}

						options.handler.call(context, evt);
					};

					fullHandler = function (evt) {
						var closestContainer, matches;

						if (!context.alive) {
							// zombie listener!
							this.log({
								type: `warning`,
								message: `Ignoring DOM event callback, owner.alive is false`,
							});

							listener.remove();
							return;
						}

						if (options.matchContainer) {
							closestContainer = evt.target;
							while (this.isElement(closestContainer)) {
								if (closestContainer === container) {
									// in our own container, move on
									matches = true;
									break;
								} else if (this.includes(activeContainers, closestContainer)) {
									// an intercepting instance's container
									matches = false;
									break;
								} else {
									// continue traversing up the DOM tree
									closestContainer = closestContainer.parentNode;
								}
							}

							if (matches !== true) {
								this.log({
									priority: 10,
									type: `warning`,
									message: `DOM event: skipping handler, container doesn't match.`,
								});
								// evt.preventDefault();
								return;
							}
						}

						options.handler.call(context, evt);
					};

					proxyHandler = this.includes(QUICK_EVENTS, options.event) ? quickHandler : fullHandler;
					proxyHandler = this.bind(proxyHandler);

					if (this.includes(HAMMER_EVENTS, options.event)) {
						hammer = new Hammer(options.target);
						hammer.on(options.event, proxyHandler);
					} else if (this.isString(options.event)) {
						// use jquery to assign the event listener to the target
						if (options.passive && supportsPassive) {
							options.target.addEventListener(options.event, proxyHandler, { passive: true });
						} else {
							jQuery(options.target).on(options.event, options.filter, null, proxyHandler);
						}
					} else if (!options.trigger) {
						throw new Error(
							`DOM.on(): no way to trigger handler without options.event<String> or options.trigger = true`
						);
					}

					listener.remove = function () {
						// create a function to remove the listener, and remove it from the eventBucket
						if (_this.includes(HAMMER_EVENTS, options.type)) {
							hammer.off(options.event, proxyHandler);
							hammer.destroy();
						} else {
							// remove the event listeners via jquery
							if (options.passive && supportsPassive) {
								options.target.removeEventListener(options.event, proxyHandler, { passive: true });
							} else {
								jQuery(options.target).off(options.event, options.filter, proxyHandler);
							}
						}

						// use direct access to the created listener, to filter it out of the eventBucket
						eventBucket = _this.without(eventBucket, listener);
					};

					if (options.trigger !== false) {
						// trigger the handler manually
						triggerHandler = function () {
							// the function responsible for triggering the handler manually
							var doTrigger = false;

							if (options.trigger === `visible`) {
								doTrigger = this.isVisible(options.target);
							} else if (options.trigger === true) {
								doTrigger = true;
							} else {
								throw new Error(`Invalid options.trigger argument, supports true and "visible".`);
							}

							if (doTrigger) {
								if (this.isString(options.event, 1) && this.isObject(options.target)) {
									// we have a target
									if (this.isString(options.filter)) {
										// there's a css selector for event delegation
										this.trigger({
											event: options.event,
											target: this.get(options.filter, container),
										});
									} else {
										// no selector, trigger on the target itself
										this.trigger({
											event: options.event,
											target: options.target,
										});
									}
								} else {
									options.handler.call(context); // call the handler manually
									listener.remove(); // remove
								}
							} else {
								// shouldn't trigger yet
								return this.defer({
									// delay and check again
									duration: 100,
									then: triggerHandler,
								});
							}
						};

						this.defer({
							duration: 10,
							then: triggerHandler,
							done: true,
						});
					}

					eventBucket.push(listener);
					return listener;
				},
			},

			/**
			 * Remove event listeners.
			 *
			 * @method DOM#off
			 * @param {Object} [options] - Filter criteria (all listeners if empty)
			 * @param {Element} [options.target] - Remove listeners on this target
			 * @param {string} [options.event] - Remove listeners for this event
			 * @param {string} [options.filter] - Remove listeners with this filter
			 * @param {Function} [options.handler] - Remove this specific handler
			 */
			off: {
				public: true,
				defaults: {},
				value(context, options) {
					this.defaults(options, {
						container: context.container,
					});

					var query = this.chain([`owner`, `target`, `event`, `filter`, `handler`])
						.reduce(
							function (query, keyword) {
								if (this.isDefined(options[keyword])) {
									query[keyword] = options[keyword];
								}

								return query;
							},
							{
								// initial query
								owner: context,
							}
						)
						.value();

					this.chain(eventBucket) // start with all listeners
						.filter(query) // filter own to the matching ones
						.each(function (listener) {
							listener.remove(); // NOTE, removes itself from the eventBucket
						});
				},
			},

			/**
			 * Show element with optional transition.
			 *
			 * @method DOM#show
			 * @param {Object} [options] - Show options
			 * @param {Element|Element[]} [options.target] - Element(s) to show (defaults to this.container)
			 * @param {string} [options.selector] - CSS selector for target
			 * @param {string} [options.transition='none'] - Transition type: 'none', 'fade', 'slide', 'pop'
			 * @param {number} [options.duration=200] - Transition duration (ms)
			 * @param {string} [options.easing='ease-in-out'] - CSS easing function
			 * @param {string} [options.direction='top'] - Direction for 'slide' transition
			 * @returns {Promise<Element|Element[]>} Resolves when shown
			 */
			show: {
				async: true,
				public: true,
				defaults: {
					transition: `none`,
					easing: `ease-in-out`,
					duration: 200,
					direction: `top`,
				},
				value(context, options) {
					if (this.isUndefined(options.target)) {
						if (this.isUndefined(options.selector)) {
							options.target = context.container;
						} else {
							let container = options.container;
							if (this.isUndefined(container)) {
								container = context.container;
							}
							options.target = this.select(options.selector, container).value();
						}
					}

					if (!this.isElement(options.target) && !this.isArray(options.target)) {
						throw new Error(`DOM.show: invalid target specified`);
					}

					var isArray = this.isArray(options.target), // used to decide between resolving an array or element
						targets = isArray ? options.target : [options.target], // a deferred action, to be resolved when all elements have completed transitioning
						promise;

					promise = this.chain(targets)
						.map(function (target) {
							// iterate over each element, and return either the element, or a promise for it
							var styles = target.style,
								parent = target.parentNode,
								dimensions = this.getDimensions(target),
								transition, // used in the case of an active transition
								delayPromise, // used if a delay is needed
								inlineStyles,
								onComplete,
								listener,
								deferred;

							transition = this.find(activeTransitions, function (transition) {
								return transition.target === target;
							});

							if (this.isObject(transition)) {
								if (transition.type === `show`) {
									// already being transitioned in
									return transition.promise; // use the existing promise
								} else {
									// show after it's done being hidden
									delayPromise = transition.promise; // wait until the hide is complete
								}
							} else if (
								options.transition === `none` ||
								dimensions.isVisible ||
								!(this.isElement(parent) && this.isVisible(parent))
							) {
								// no transition, element is already visible, or doesn't have a visible parent
								if (styles.display === `none`) {
									// there's an inline style hiding the target
									styles.display = ``; // remove it
								} // NOTE: this method will note show elements hidden with css; doesn't manually set display to 'block'

								return target; // resolve immediately
							}

							return this.when({
								promise: delayPromise,
								then: function () {
									styles.opacity = `0`;
									if (styles.display === `none`) {
										// being hidden in-line
										styles.display = ``; // remove the in-line rule
									}

									inlineStyles = this.reduce(
										CSS_KEYS,
										function (obj, key) {
											// cache values that are specifically in-lined, that may need to be restored
											obj[key] = styles[key];
											return obj;
										},
										{}
									); // start with an empty object

									inlineStyles.opacity = ``;

									if (inlineStyles.display === `none`) {
										inlineStyles.display = ``;
										styles.display = ``;
									}

									deferred = this.Deferred();
									let deferredPending = true;
									onComplete = function (evt) {
										listener.remove();

										this.each(CSS_KEYS, function (key) {
											styles[key] = this.isString(inlineStyles[key]) ? inlineStyles[key] : ``;
										});

										this.each(TRANSITION_KEYS, function (key) {
											styles[key] = ``;
										});

										if (options.remove) {
											this.remove(target);
										}

										deferred.resolve(target);
										deferredPending = false;
									};

									styles.display = ``; // remove the inline 'none'
									if (dimensions.display === `none`) {
										// still hidden via CSS
										styles.display = `block`; // default to block
									}

									// avoid risking transitioning TO this pre-transition state
									styles[KEYWORDS.transition + `Duration`] = `0`;
									styles[KEYWORDS.transition + `TimingFunction`] = options.easing;

									switch (
										options.transition // prep work while the target is invisible
									) {
										case `fade`:
											styles[KEYWORDS.transition + `Property`] = `opacity`;
											break;

										case `width`:
											styles[KEYWORDS.transform + `Origin`] = `left`;
											styles[KEYWORDS.transition + `Property`] = `opacity, transform`;
											break;

										case `height`:
											styles[KEYWORDS.transform + `Origin`] = `top`;
											styles[KEYWORDS.transition + `Property`] = `opacity, transform`;
											break;

										case `pop`:
											styles[KEYWORDS.transition + `Property`] = `opacity, transform`;
											break;

										case `slide`:
											styles[KEYWORDS.transition + `Property`] = `opacity, transform`;
											break;
									}

									this.defer({
										then: function () {
											listener = this.on({
												// add a listener to catch when the transition is complete
												container: target,
												event: KEYWORDS.transitionEvent,
												target,
												handler: onComplete,
											});

											styles[KEYWORDS.transition + `Duration`] = options.duration + `ms`;
											switch (
												options.transition // prep work while the target is invisible
											) {
												case `fade`:
													styles[KEYWORDS.transition + `Property`] = `opacity`;
													break;

												case `width`:
													styles[KEYWORDS.transform] = `scaleX(0)`;
													break;

												case `height`:
													styles[KEYWORDS.transform] = `scaleY(0)`;
													break;

												case `pop`:
													styles[KEYWORDS.transform] = `scale(0.6, 0.6)`;
													break;

												case `slide`:
													this.defaults(options, {
														direction: `top`,
													});

													switch (options.direction) {
														case `top`:
															styles[KEYWORDS.transform] = `translate3d(0,-100%,0)`;
															break;

														case `bottom`:
															styles[KEYWORDS.transform] = `translate3d(0,100%,0)`;
															break;

														case `left`:
															styles[KEYWORDS.transform] = `translate3d(-100%,0,0)`;
															break;

														case `right`:
															styles[KEYWORDS.transform] = `translate3d(100%,0,0)`;
															break;
													}

													break;
											}

											return this.defer({
												duration: 30,
												then: function () {
													switch (options.transition) {
														case `fade`:
															styles.opacity = `1`;
															break;

														case `width`:
															styles[KEYWORDS.transform] = `scaleX(1)`;
															styles.opacity = `1`;
															break;

														case `height`:
															styles[KEYWORDS.transform] = `scaleY(1)`;
															styles.opacity = `1`;
															break;

														case `pop`:
															styles.opacity = `1`;
															styles[KEYWORDS.transform] = `scale(1, 1)`;
															break;

														case `slide`:
															styles[KEYWORDS.transform] = `translate3d(0,0,0)`;
															styles.opacity = `1`;
															break;
													}

													this.defer({
														// precautionary callback
														duration: options.duration + 20,
														then: function () {
															if (deferredPending) {
																this.log({
																	priority: 10,
																	type: `warning`,
																	message: `DOM.show() failsafe timeout used`,
																});

																onComplete.call(this);
															}
														},
														done: true,
													});
												},
											});
										},
										done: true,
									}); // end defer()

									return deferred.promise;
								},
							});
						})
						.whenAll({
							then: function (targets) {
								// all transitions have resolved
								return isArray ? targets : targets[0];
							},
						});

					return promise;
				},
			},

			/**
			 * Hide element with optional transition.
			 *
			 * @method DOM#hide
			 * @param {Object} [options] - Hide options
			 * @param {Element|Element[]} [options.target] - Element(s) to hide (defaults to this.container)
			 * @param {string} [options.selector] - CSS selector for target
			 * @param {string} [options.transition='none'] - Transition type: 'none', 'fade', 'slide', 'pop'
			 * @param {number} [options.duration=200] - Transition duration (ms)
			 * @param {string} [options.easing='ease-in-out'] - CSS easing function
			 * @param {string} [options.direction='top'] - Direction for 'slide' transition
			 * @param {boolean} [options.remove=false] - Remove element from DOM after hiding
			 * @param {boolean} [options.important=false] - Use !important on display:none
			 * @returns {Promise<Element|Element[]>} Resolves when hidden
			 */
			hide: {
				async: true,
				public: true,
				defaults: {
					important: false,
					transition: `none`,
					easing: `ease-in-out`,
					duration: 200,
					direction: `top`,
					remove: false,
					empty: false,
				},
				value(context, options) {
					if (this.isUndefined(options.target)) {
						if (this.isUndefined(options.selector)) {
							options.target = context.container;
						} else {
							let container = options.container;
							if (this.isUndefined(container)) {
								container = context.container;
							}
							options.target = this.select(options.selector, container).value();
						}
					}

					if (!this.isElement(options.target) && !this.isArray(options.target)) {
						throw new Error(`DOM.hide: invalid target specified`);
					}

					var isArray = this.isArray(options.target), // used to decide between resolving an array or element
						targets = isArray ? options.target : [options.target]; // a deferred action, to be resolved when all elements have completed transitioning

					return this.chain(targets)
						.map(function (target) {
							var styles = target.style,
								parent = target.parentNode,
								dimensions = this.getDimensions(target),
								inlineStyles,
								delayPromise,
								onComplete,
								transition,
								listener,
								deferred;

							transition = this.find(activeTransitions, function (transition) {
								return transition.target === target;
							});

							if (this.isObject(transition)) {
								if (transition.type === `hide`) {
									// already being transitioned in
									return transition.promise; // same goal, use the existing promise
								} else {
									// hide after it's done being shown
									delayPromise = transition.promise; // wait until the hide is complete
								}
							} else if (
								options.transition === `none` ||
								!dimensions.isVisible ||
								!(this.isElement(parent) && this.isVisible(parent))
							) {
								// no transition, element is already hidden, or doesn't have a visible parent
								// manually hide it, in case it should be hidden within its already hidden parent
								const { important } = options;
								const priority = important ? `important` : ``;
								styles.setProperty(`display`, `none`, priority);

								if (options.remove) {
									this.remove(target);
								}

								return target; // resolve with the target
							}

							deferred = this.Deferred();
							let deferredPending = true;
							activeTransitions.unshift({
								target,
								type: `hide`,
								promise: deferred.promise,
							});

							return this.when({
								promise: delayPromise,
								then: function () {
									inlineStyles = this.reduce(
										CSS_KEYS,
										function (obj, key) {
											// cache values that are specifically in-lined, that may need to be restored
											obj[key] = styles[key];
											return obj;
										},
										{}
									); // start with an empty object

									onComplete = function (evt) {
										listener.remove();

										inlineStyles.display = `none`; // ensure the element stays hidden

										this.each(CSS_KEYS, function (key) {
											styles[key] = this.isString(inlineStyles[key]) ? inlineStyles[key] : ``;
										});

										this.each(TRANSITION_KEYS, function (key) {
											styles[key] = ``;
										});

										if (options.remove) {
											this.remove(target);
										}

										deferred.resolve(target);
										deferredPending = false;
									};

									// avoid risking transitioning TO this pre-transition state
									// styles.display = 'block';
									styles[KEYWORDS.transition + `Duration`] = `0`;
									switch (options.transition) {
										case `fade`:
											styles[KEYWORDS.transition + `Property`] = `opacity`;
											break;

										case `width`:
											styles[KEYWORDS.transform + `Origin`] = `left`;
											styles[KEYWORDS.transition + `Property`] = `opacity, transform`;
											break;

										case `height`:
											styles[KEYWORDS.transform + `Origin`] = `top`;
											styles[KEYWORDS.transition + `Property`] = `opacity, transform`;
											break;

										case `pop`:
											styles[KEYWORDS.transition + `Property`] = `opacity, transform`;
											break;

										case `slide`:
											styles[KEYWORDS.transition + `Property`] = `opacity, transform`;
											break;
									}

									this.defer({
										then: function () {
											listener = this.on({
												// start listening for the completion of the css transition
												container: target,
												event: KEYWORDS.transitionEvent,
												target,
												handler: onComplete,
											});

											styles[KEYWORDS.transition + `TimingFunction`] = options.easing;

											switch (options.transition) {
												case `fade`:
													styles.opacity = `1`;
													break;

												case `width`:
													styles[KEYWORDS.transform] = `scaleX(1)`;
													styles.opacity = `1`;
													break;

												case `height`:
													styles[KEYWORDS.transform] = `scale3d(1, 1, 1)`;
													styles.opacity = `1`;
													break;

												case `height-smooth`:
													styles.height = target.clientHeight + `px`;
													break;

												case `pop`:
													styles[KEYWORDS.transform] = `scale(1, 1)`;
													styles.opacity = `1`;
													break;

												case `slide`:
													styles[KEYWORDS.transform] = `translate3d(0,0,0)`;
													styles.opacity = `1`;
													break;
											}

											this.defer({
												duration: 10,
												then: function () {
													styles[KEYWORDS.transition + `Duration`] = options.duration + `ms`;
													switch (options.transition) {
														case `fade`:
															styles.opacity = `0`;
															break;

														case `width`:
															styles[KEYWORDS.transform] = `scaleX(0)`;
															styles.opacity = `0`;
															break;

														case `height`:
															styles[KEYWORDS.transform] = `scale3d(1, 0, 1)`;
															styles.opacity = `0`;
															break;

														case `height-smooth`:
															styles.height = `0`;
															styles.opacity = `0`;
															break;

														case `pop`:
															styles[KEYWORDS.transform] = `scale(0.6, 0.6)`;
															styles.opacity = `0`;
															break;

														case `slide`:
															this.defaults(options, {
																direction: `top`,
															});

															styles.opacity = `0`;

															switch (options.direction) {
																case `top`:
																	styles[KEYWORDS.transform] = `translate3d(0,-100%,0)`;
																	break;

																case `bottom`:
																	styles[KEYWORDS.transform] = `translate3d(0,100%,0)`;
																	break;

																case `left`:
																	styles[KEYWORDS.transform] = `translate3d(-100%,0,0)`;
																	break;

																case `right`:
																	styles[KEYWORDS.transform] = `translate3d(100%,0,0)`;
																	break;
															}
															break;
													}
												},
											});

											this.defer({
												// precautionary callback
												duration: options.duration + 100,
												then: function () {
													if (deferredPending) {
														this.log({
															priority: 10,
															type: `warning`,
															message: `DOM.hide() failsafe timeout used`,
														});

														onComplete.call(this);
													}
												},
												done: true,
											});
										},
										done: true,
									}); // end defer()

									return deferred.promise;
								},
								fin: function () {
									this.extract(activeTransitions, {
										// remove this transition from the list
										promise: deferred.promise,
									});
								},
							});
						})
						.whenAll({
							// waiting for the map of all transitions to complete
							then: function (targets) {
								// all transitions have resolved
								return isArray ? targets : targets[0];
							},
						});
				},
			},

			/**
			 * @method DOM#getListeners
			 */
			getListeners: {
				value() {
					// return a new array, so modifying the array won't matter, but access to the listeners is still by reference
					return this.map(eventBucket, function (listener) {
						return listener;
					});
				},
			},

			/**
			 * Append content to element.
			 *
			 * @method DOM#append
			 * @param {string|Element} content - Content to append
			 * @param {Element} [element] - Target element (defaults to this.container)
			 * @returns {jQuery} jQuery object
			 */
			append: {
				public: true,
				value(context, content, element) {
					if (!this.isElement(element)) {
						element = context.container;
					}

					return jQuery(element).append(content);
				},
			},

			/**
			 * @method DOM#syncActiveContainers
			 */
			syncActiveContainers: {
				value() {
					this.chain(instanceBucket)
						.filter(function (registrant) {
							// filter out non-interceptors
							return registrant.interceptEvents;
						})
						.map(function (registrant) {
							// map to an array of DOM elements
							return registrant.instance.container;
						})
						.unique() // remove duplicates
						.tap(function (list) {
							activeContainers = list;
						});
				},
			},

			/**
			 * Register instance with DOM for event interception tracking.
			 *
			 * NOTE: Called automatically by Controller during construction.
			 *
			 * @method DOM#register
			 * @param {Object} [options] - Registration options
			 * @param {boolean} [options.interceptEvents] - Block events from bubbling through this container
			 */
			register: {
				public: true,
				private: false,
				value(context, options) {
					// called upon instance construction, for any special configuration
					options = this.defaults(options, {
						interceptEvents: this.isElement(context.container), // can't intercept without a container element to check
					});

					instanceBucket.push({
						instance: context,
						interceptEvents: options.interceptEvents, // if false, this will be excluded when checking options.matchContainer
					});

					this.syncActiveContainers();
				},
			},

			/**
			 * Replace element with another.
			 *
			 * @method DOM#replace
			 * @param {Element} target - Element to replace
			 * @param {Element} replacement - New element
			 * @returns {Element} The replacement element
			 */
			replace: {
				public: true,
				value(context, target, replacement) {
					if (!(this.isElement(target) && this.isElement(replacement))) {
						throw new Error(`DOM.replace(target, replacement): both arguments are required`);
					}

					return target.parentNode.replaceChild(replacement, target);
				},
			},

			/**
			 * Get vendor-specific CSS property names (for transitions, transforms).
			 *
			 * @method DOM#getVendorKeywords
			 * @returns {Object} Vendor keywords for current browser
			 */
			getVendorKeywords: {
				public: true,
				private: false,
				value(context) {
					return KEYWORDS;
				},
			},

			/**
			 * Get scroll position of element or viewport.
			 *
			 * @method DOM#getScrollTop
			 * @param {Element} [element] - Element to check (defaults to viewport)
			 * @returns {number} Scroll position in pixels
			 */
			getScrollTop: {
				public: true,
				value(context, element) {
					let amount;

					if (this.isElement(element)) {
						// a target was specified
						amount = element.scrollTop;
					} else if (this.isElement(defaultScrollElement)) {
						// no target, check if the primary scroll container has been found
						amount = defaultScrollElement.scrollY;
					} else {
						// no default target, try to calculate
						// check the body
						amount = document.body.scrollTop;

						if (amount !== 0) {
							// body is being used for scroll behavior (probably iOS)
							defaultScrollElement = document.body;
						} else {
							defaultScrollElement = window;
							amount = window.scrollY;
						}
					}

					return amount;
				},
			},

			/**
			 * Defer callback until element has dimensions (useful for layout calculations).
			 *
			 * @method DOM#deferUICalculations
			 * @param {Element} target - Element to check
			 * @param {Function} callback - Function to call when ready
			 */
			deferUICalculations: {
				public: true,
				value(context, target, callback) {
					const epsilon = 1;

					if (this.getDimensions(target).width <= epsilon) {
						this.defer({
							duration: 100,
							then: () => {
								this.deferUICalculations(target, callback);
							},
						});
					} else {
						callback();
					}
				},
			},

			/**
			 * Singleton lifecycle hook - called when an instance using DOM is destroyed.
			 *
			 * NOTE: This is an internal framework hook, not called by user code.
			 * Auto-invoked via registerDependency pattern.
			 *
			 * @method DOM#onDie
			 * @param {Object} dyingInstance - Instance being destroyed
			 * @private
			 */
			onDie: {
				private: false,
				public: true,
				value(context) {
					this.off({
						// Remove all listeners
						owner: context, // regardless of anything else, all listeners removed
					});

					this.extract(instanceBucket, {
						// remove from the list of registered instances
						instance: context,
					});

					this.syncActiveContainers();

					var styleCache = this.data.styleCache;

					this.each(styleCache, function (instances, href) {
						styleCache[href] = this.without(instances, context);

						if (styleCache[href].length === 0) {
							window.setTimeout(
								this.bind(function () {
									if (styleCache[href] && styleCache[href].length === 0) {
										this.select(`link[href="` + href + `"]`, document).remove();
										delete styleCache[href];
									}
								}),
								UNLOAD_GRACE_PERIOD
							);
						}
					});
				},
			},
		},
	});
});

//# sourceMappingURL=DOM.js.map